import {addDays} from 'date-fns/addDays'
import {addMonths} from 'date-fns/addMonths'
import {differenceInDays} from 'date-fns/differenceInDays'
import {format} from 'date-fns/format'
import {isValid} from 'date-fns/isValid'
import {parseISO} from 'date-fns/parseISO'
import {curry, pipe} from 'ramda'

import {MAX_DATE_DIFFERENCE, MAX_MONTHS_COUNT} from '../datePicker/config'
import {isRateChecker} from '../rateChecker'
import {
  dateStringToMiddayDate,
  dateToMiddayDate,
  ensureCheckInOnOrAfterToday,
  UTS_DATE_FORMAT
} from '../utils/date'
import {UrlParamsType} from '../utils/url/types/UrlParams'
import {DateValidationReason} from './dateValidationReason'

/**
 * @param urlParams
 * Inspects URL params and returns the first matching date validation reason.
 * Returns null for rate checker URLs (gsite=verification).
 * Priority order:
 * 1. Rate checker → null
 * 2. No checkIn → null (handled by existing booking hint logic)
 * 3. Invalid format → InvalidFormat
 * 4. CheckIn in the past (more than 1 day before today) → DatesInPast
 * 5. CheckIn or checkOut beyond MAX_MONTHS_COUNT months from today → TooFarInFuture
 * 6. Stay length exceeds MAX_DATE_DIFFERENCE → StayTooLong
 * 7. Otherwise → null
 */
export const getDateValidationReason = (
  urlParams: Record<string, string | number | Array<number | string>>
): DateValidationReason | null => {
  if (isRateChecker(urlParams)) return null

  const checkIn = urlParams.checkIn as string | undefined
  const checkOut = urlParams.checkOut as string | undefined

  if (!checkIn) return null

  const checkInDate = dateStringToMiddayDate(checkIn)
  const checkOutDate = checkOut ? dateStringToMiddayDate(checkOut) : null

  if (!isValid(checkInDate) || (checkOut && !isValid(checkOutDate))) {
    return DateValidationReason.InvalidFormat
  }

  const today = dateToMiddayDate(new Date())
  const checkInDaysSinceToday = differenceInDays(checkInDate, today)

  if (checkInDaysSinceToday < -1) {
    return DateValidationReason.DatesInPast
  }

  const maxFutureDate = addMonths(today, MAX_MONTHS_COUNT)

  if (
    differenceInDays(checkInDate, maxFutureDate) > 0 ||
    (checkOutDate && differenceInDays(checkOutDate, maxFutureDate) > 0)
  ) {
    return DateValidationReason.TooFarInFuture
  }

  if (checkOutDate) {
    const stayLength = differenceInDays(checkOutDate, checkInDate)
    if (stayLength > MAX_DATE_DIFFERENCE) {
      return DateValidationReason.StayTooLong
    }
  }

  return null
}

const dateStringOrNull = (dateString: string): Date | null => {
  const date = dateStringToMiddayDate(dateString)
  return isValid(date) ? date : null
}

/**
 * Determines the earliest valid check-in date based on the given check-in date and the current time.
 * If the check-in date is more than 1 day before today or beyond MAX_MONTHS_COUNT months in the
 * future, returns null so callers can clear the dates and let SAPI apply its own default-date logic.
 * If the check-in date is yesterday it is preserved (the FE allows the 1-day-in-past edge case).
 * @param checkIn - The check-in date to evaluate.
 * @param isCheckInForced - Whether the check-in date should be forced without validation.
 * @returns The valid check-in date, or null when out of range.
 */
export const determineValidCheckInDate = (
  checkIn: Date,
  isCheckInForced = false
): Date | null => {
  if (isCheckInForced) return checkIn

  const today = dateToMiddayDate(new Date())
  const checkInDaysSinceToday = differenceInDays(checkIn, today)

  if (checkInDaysSinceToday < -1) return null

  const maxFutureDate = addMonths(today, MAX_MONTHS_COUNT)
  if (differenceInDays(checkIn, maxFutureDate) > 0) return null

  return checkIn
}

/**
 * Returns a valid check-out date based on a valid check-in date, while ensuring that the length of stay is maintained.
 * @param checkIn - check-in date to be compared
 * @param validCheckIn - valid checkIn date from determineValidCheckInDate function
 * @param checkOut - check-out date to be compared
 * @returns A valid checkout date
 */
const ensureCheckOutAfterCheckIn = curry(
  (checkIn: Date, validCheckIn: Date, checkOut: Date) => {
    const originalLengthOfStay = differenceInDays(checkOut, checkIn)
    const today = dateToMiddayDate(new Date())

    // If the checkIn date is not valid or length of stay is less than one night or the checkIn date is before yesterday
    if (
      !originalLengthOfStay ||
      originalLengthOfStay < 1 ||
      differenceInDays(checkIn, today) < -1
    )
      return addDays(validCheckIn, 1)

    return addDays(validCheckIn, originalLengthOfStay)
  }
)

const formatDate = (date: Date): string => format(date, UTS_DATE_FORMAT)

const formatDateOrUndefined = (date: Date | null): string | undefined =>
  date ? formatDate(date) : undefined

/**
 * Validates and adjusts search parameter dates. When dates fall outside acceptable
 * bounds (invalid format, more than 1 day in the past, beyond MAX_MONTHS_COUNT
 * months in the future, or a stay longer than MAX_DATE_DIFFERENCE), both dates are
 * cleared so SAPI applies its own default-date logic. Rate checker URLs skip
 * validation and pass through unchanged.
 * @param searchParams - Search parameters containing date strings
 * @returns Search parameters with dates either preserved, adjusted, or cleared
 */
export const validateSearchParamDates = <T extends Partial<UrlParamsType>>(
  searchParams: T
): T => {
  const {checkIn, checkOut} = searchParams
  const isRateCheckerFromUrl = isRateChecker(searchParams)
  if (!checkIn) return searchParams

  let validCheckIn: string | undefined = pipe(
    dateStringOrNull,
    checkIn =>
      checkIn ? determineValidCheckInDate(checkIn, isRateCheckerFromUrl) : null,
    formatDateOrUndefined
  )(checkIn)

  let validCheckOut: string | undefined
  if (validCheckIn) {
    validCheckOut = pipe(
      dateStringOrNull,
      date =>
        date
          ? ensureCheckOutAfterCheckIn(
              parseISO(checkIn),
              parseISO(validCheckIn as string),
              date
            )
          : null,
      formatDateOrUndefined
    )(checkOut || checkIn)
  }

  if (!isRateCheckerFromUrl && validCheckIn && validCheckOut) {
    const today = dateToMiddayDate(new Date())
    const maxFutureDate = addMonths(today, MAX_MONTHS_COUNT)
    const checkOutDate = dateStringToMiddayDate(validCheckOut)
    const checkInDate = dateStringToMiddayDate(validCheckIn)

    // Clear both dates if checkout is too far in the future
    if (differenceInDays(checkOutDate, maxFutureDate) > 0) {
      validCheckIn = undefined
      validCheckOut = undefined
    }

    // Clear both dates if stay exceeds MAX_DATE_DIFFERENCE nights
    const stayLength = differenceInDays(checkOutDate, checkInDate)
    if (stayLength > MAX_DATE_DIFFERENCE) {
      validCheckIn = undefined
      validCheckOut = undefined
    }
  }

  return {
    ...searchParams,
    checkIn: validCheckIn,
    checkOut: validCheckOut
  }
}

// TODO (Core): Add unit tests and documentation for this function
/**
 * Calculates check-out date based on check-in date and number of nights
 * @param checkIn - Check-in date string
 * @param numberOfNights - Number of nights to stay
 * @returns Check-out date
 */
export const getCheckOutDate = (checkIn: string, numberOfNights: number) => {
  const checkInDate = dateStringToMiddayDate(checkIn)
  const checkOutDate = new Date(checkInDate)
  checkOutDate.setDate(checkInDate.getDate() + numberOfNights)
  return checkOutDate
}

const SEARCH_RANGE = 14 // days

// TODO (Core): Add unit tests for this function
/**
 * Returns the initial search dates, based on the check in date.
 * If the check in date is further ahead than today + SEARCH_RANGE,
 * the search dates will be centered around the check in date.
 * Otherwise, the search dates will be from today.
 * @param checkIn
 * @returns
 */
export const getInitialSearchDates = (checkIn: string) => {
  let startDate = null
  let endDate = null

  const today = dateToMiddayDate(new Date())
  const isCheckInPastMidSearchRange =
    differenceInDays(new Date(checkIn), addDays(today, SEARCH_RANGE)) > 0

  if (isCheckInPastMidSearchRange) {
    startDate = ensureCheckInOnOrAfterToday(
      addDays(new Date(checkIn), -SEARCH_RANGE / 2)
    )
    endDate = addDays(new Date(checkIn), SEARCH_RANGE / 2)
  } else {
    startDate = today
    endDate = addDays(today, SEARCH_RANGE)
  }

  return {
    startDate,
    endDate
  }
}
